export const NAVIGATION_SESSION_KEY = "trekkin_active_navigation_v1";
export const NAVIGATION_SESSION_VERSION = 1;

const MAX_ROUTE_POINTS = 12000;

function validPoint(point) {
  const lat = Number(point?.lat);
  const lon = Number(point?.lon ?? point?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const ele = Number(point?.ele ?? point?.elevation ?? point?.alt);
  return {
    lat,
    lon,
    ...(Number.isFinite(ele) ? { ele } : {}),
  };
}

export function normalizeNavigationPoints(points, maxPoints = MAX_ROUTE_POINTS) {
  const normalized = (Array.isArray(points) ? points : [])
    .map(validPoint)
    .filter(Boolean);
  const limit = Math.max(2, Number(maxPoints) || MAX_ROUTE_POINTS);
  if (normalized.length <= limit) return normalized;

  const output = [normalized[0]];
  const step = (normalized.length - 1) / (limit - 1);
  for (let index = 1; index < limit - 1; index += 1) {
    output.push(normalized[Math.round(index * step)]);
  }
  output.push(normalized[normalized.length - 1]);
  return output;
}

function normalizeWaypoint(target) {
  if (!target) return null;
  const point = validPoint(target);
  if (!point) return null;
  return {
    id: target?.id ?? null,
    name: String(target?.name || target?.nome || "Waypoint"),
    ...point,
  };
}

function normalizePending(pending) {
  const target = normalizeWaypoint(pending?.target);
  const entry = validPoint(pending?.entry);
  if (!target || !entry) return null;
  return {
    target,
    entry,
    thresholdMeters: Number(pending?.thresholdMeters) || 200,
    createdAt: Number(pending?.createdAt) || Date.now(),
  };
}

function normalizeTrailData(trail, points) {
  if (!trail) return null;
  return {
    id: trail?.id ?? null,
    nome: String(trail?.nome || trail?.name || trail?.title || "Trilho"),
    name: String(trail?.name || trail?.nome || trail?.title || "Trilho"),
    activityMode: trail?.activityMode || trail?.mode || null,
    __source: trail?.__source || null,
    points,
  };
}

export function buildNavigationSession(state, routeSnapshot = null) {
  const target = normalizeWaypoint(state?.navWaypointTarget);
  const pending = normalizePending(state?.navWaypointPending);
  const queue = (Array.isArray(state?.navWaypointQueue)
    ? state.navWaypointQueue
    : [])
    .map(normalizeWaypoint)
    .filter(Boolean);
  const route = normalizeNavigationPoints(routeSnapshot?.path);

  if (target || pending) {
    return {
      version: NAVIGATION_SESSION_VERSION,
      type: "waypoint",
      updatedAt: Date.now(),
      target,
      pending,
      queue,
      route,
    };
  }

  const trailBase = normalizeNavigationPoints(state?.trailBase);
  if (trailBase.length > 1) {
    return {
      version: NAVIGATION_SESSION_VERSION,
      type: "trail",
      updatedAt: Date.now(),
      trailBase,
      trailData: normalizeTrailData(state?.trailData, trailBase),
      route,
    };
  }

  return null;
}

export function saveNavigationSession(storage, state, routeSnapshot = null) {
  if (!storage) return null;
  const session = buildNavigationSession(state, routeSnapshot);
  if (!session) {
    storage.removeItem(NAVIGATION_SESSION_KEY);
    return null;
  }
  storage.setItem(NAVIGATION_SESSION_KEY, JSON.stringify(session));
  return session;
}

export function loadNavigationSession(storage) {
  if (!storage) return null;
  try {
    const parsed = JSON.parse(storage.getItem(NAVIGATION_SESSION_KEY) || "null");
    if (Number(parsed?.version) !== NAVIGATION_SESSION_VERSION) return null;
    if (parsed?.type === "waypoint") {
      const target = normalizeWaypoint(parsed?.target);
      const pending = normalizePending(parsed?.pending);
      if (!target && !pending) return null;
      return {
        ...parsed,
        target,
        pending,
        queue: (Array.isArray(parsed?.queue) ? parsed.queue : [])
          .map(normalizeWaypoint)
          .filter(Boolean),
        route: normalizeNavigationPoints(parsed?.route),
      };
    }
    if (parsed?.type === "trail") {
      const trailBase = normalizeNavigationPoints(parsed?.trailBase);
      if (trailBase.length < 2) return null;
      return {
        ...parsed,
        trailBase,
        trailData: normalizeTrailData(parsed?.trailData, trailBase),
        route: normalizeNavigationPoints(parsed?.route),
      };
    }
  } catch {}
  return null;
}

export function clearNavigationSession(storage) {
  try {
    storage?.removeItem?.(NAVIGATION_SESSION_KEY);
  } catch {}
}
